'use client';

import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useI18n } from '@/providers/i18n';
import { useControllableState } from '@radix-ui/react-use-controllable-state';
import { ChevronsUpDownIcon } from 'lucide-react';
import {
  type ComponentProps,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

type ComboboxData = {
  label: string;
  value: string;
};

type ComboboxContextType = {
  data: ComboboxData[];
  type: string;
  value: string;
  onValueChange: (value: string) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  width: number;
  setWidth: (width: number) => void;
};

const ComboboxContext = createContext<ComboboxContextType>({
  data: [],
  type: 'item',
  value: '',
  onValueChange: () => {},
  open: false,
  onOpenChange: () => {},
  width: 200,
  setWidth: () => {},
});

export type ComboboxProps = ComponentProps<typeof Popover> & {
  data: ComboboxData[];
  type: string;
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

export const Combobox = ({
  data,
  type,
  defaultValue,
  value: controlledValue,
  onValueChange: controlledOnValueChange,
  defaultOpen = false,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  ...props
}: ComboboxProps) => {
  const [value, onValueChange] = useControllableState({
    defaultProp: defaultValue ?? '',
    prop: controlledValue,
    onChange: controlledOnValueChange,
  });
  const [open, onOpenChange] = useControllableState({
    defaultProp: defaultOpen,
    prop: controlledOpen,
    onChange: controlledOnOpenChange,
  });
  const [width, setWidth] = useState(200);

  return (
    <ComboboxContext.Provider
      value={{
        type,
        value,
        onValueChange,
        open,
        onOpenChange,
        data,
        width,
        setWidth,
      }}
    >
      <Popover {...props} open={open} onOpenChange={onOpenChange} />
    </ComboboxContext.Provider>
  );
};

export type ComboboxTriggerProps = ComponentProps<typeof Button>;

export const ComboboxTrigger = ({
  children,
  ...props
}: ComboboxTriggerProps) => {
  const { value, data, type, setWidth, open } = useContext(ComboboxContext);
  const { t } = useI18n();
  const ref = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const newWidth = (entry.target as HTMLElement).offsetWidth;
        if (newWidth) {
          setWidth?.(newWidth);
        }
      }
    });
    if (ref.current) {
      resizeObserver.observe(ref.current);
    }
    return () => {
      resizeObserver.disconnect();
    };
  }, [setWidth, open]);

  return (
    <PopoverTrigger asChild>
      <Button variant="outline" {...props} ref={ref}>
        {children ?? (
          <span className="flex w-full items-center justify-between gap-2">
            {value
              ? data.find((item) => item.value === value)?.label
              : t('Select {{type}}...', { type: type === 'model' ? t('Model') : type })}
            <ChevronsUpDownIcon
              size={16}
              className="shrink-0 text-muted-foreground"
            />
          </span>
        )}
      </Button>
    </PopoverTrigger>
  );
};

const TYPE_LABEL_KEY: Record<string, string> = {
  model: 'Model',
  size: 'Size',
  resolution: 'Resolution',
  style: 'Style',
  language: 'Language',
  project: 'Project',
  'video-aspect-ratio': 'Aspect Ratio',
  'video-duration': 'Duration',
  'video-quality': 'Quality',
};

export type ComboboxContentProps = ComponentProps<typeof Command> & {
  popoverOptions?: ComponentProps<typeof PopoverContent>;
  title?: React.ReactNode;
  hideTitle?: boolean;
};

export const ComboboxContent = ({
  className,
  popoverOptions,
  title,
  hideTitle,
  ...props
}: ComboboxContentProps) => {
  const { type } = useContext(ComboboxContext);
  const { t } = useI18n();

  const headerNode = (() => {
    if (hideTitle) return null;
    if (title !== undefined) return title;
    const key = TYPE_LABEL_KEY[type];
    const label = key ? t(key) : type;
    return t('{{type}} Selector', { type: label });
  })();

  return (
    <PopoverContent
      className={cn('p-0', className)}
      side={popoverOptions?.side ?? 'bottom'}
      align={popoverOptions?.align ?? 'start'}
      {...popoverOptions}
    >
      {headerNode ? (
        <div className="px-2 pt-1.5 pb-1 text-[11px] font-semibold tracking-wide text-muted-foreground/70">
          {headerNode}
        </div>
      ) : null}
      <Command className="bg-transparent" {...props} />
    </PopoverContent>
  );
};

export type ComboboxInputProps = ComponentProps<typeof CommandInput>;

export const ComboboxInput = (props: ComboboxInputProps) => {
  const { type } = useContext(ComboboxContext);
  const { t } = useI18n();

  return <CommandInput placeholder={t('Search {{type}}...', { type: type === 'model' ? t('Model') : type })} {...props} />;
};

export type ComboboxListProps = ComponentProps<typeof CommandList>;

export const ComboboxList = (props: ComboboxListProps) => (
  <CommandList {...props} />
);

export type ComboboxEmptyProps = ComponentProps<typeof CommandEmpty>;

export const ComboboxEmpty = ({ children, ...props }: ComboboxEmptyProps) => {
  const { type } = useContext(ComboboxContext);
  const { t } = useI18n();

  return (
    <CommandEmpty {...props}>{children ?? t('No {{type}} found.', { type: type === 'model' ? t('Model') : type })}</CommandEmpty>
  );
};

export type ComboboxGroupProps = ComponentProps<typeof CommandGroup>;

export const ComboboxGroup = (props: ComboboxGroupProps) => (
  <CommandGroup {...props} />
);

export type ComboboxItemProps = ComponentProps<typeof CommandItem>;

export const ComboboxItem = (props: ComboboxItemProps) => {
  const { value, onValueChange, onOpenChange } = useContext(ComboboxContext);

  return (
    <CommandItem
      onSelect={(currentValue) => {
        onValueChange(currentValue);
        onOpenChange(false);
      }}
      {...props}
    />
  );
};

export type ComboboxSeparatorProps = ComponentProps<typeof CommandSeparator>;

export const ComboboxSeparator = (props: ComboboxSeparatorProps) => (
  <CommandSeparator {...props} />
);